import React from "react";
import { useNavigate } from "react-router-dom";
import { ChatStreamError } from "../../../../services/chatService";

type ChatErrorBannerProps = {
  error: ChatStreamError | string | null;
  onDismiss: () => void;
  compact?: boolean;
};

export const ChatErrorBanner: React.FC<ChatErrorBannerProps> = ({
  error,
  onDismiss,
  compact = false,
}) => {
  const navigate = useNavigate();

  if (!error) return null;
  const errorObj = typeof error === "object" && error !== null ? (error as ChatStreamError) : null;
  const errorText = typeof error === "string" ? error : errorObj?.message || "Something went wrong.";
  const isBrandDetailsError = errorObj?.type === "BRAND_DETAILS_REQUIRED";
  const isInsufficientCredits = errorObj?.type === "INSUFFICIENT_CREDITS";

  if (isInsufficientCredits && errorObj) {
    return (
      <div className={`rounded-lg border border-orange-200 bg-orange-50 ${compact ? "p-3" : "p-4"}`}>
        <div className="flex items-start gap-3">
          <div className="mt-0.5 flex-shrink-0 text-orange-500">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-orange-800">Out of Credits</p>
            <p className="mt-0.5 text-sm text-orange-700">{errorObj.message}</p>
            <button
              type="button"
              onClick={() => navigate(errorObj.actionUrl!)}
              className="mt-3 inline-flex items-center gap-1.5 rounded-md bg-orange-500 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-orange-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-1 transition-colors"
            >
              {errorObj.actionText || "Add Credits"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (isBrandDetailsError && errorObj) {
    return (
      <div className={`rounded-lg border border-amber-200 bg-amber-50 ${compact ? "p-3" : "p-4"}`}>
        <p className="mb-2 text-sm font-medium text-amber-800">{errorObj.message}</p>
        {errorObj.actionUrl && (
          <a
            href={errorObj.actionUrl}
            className="inline-block text-sm font-medium text-amber-800 underline hover:text-amber-900"
          >
            {errorObj.actionText || "Go to Dashboard"} →
          </a>
        )}
        <button
          type="button"
          onClick={onDismiss}
          className="mt-2 block text-xs text-amber-600 underline hover:text-amber-800"
        >
          Dismiss
        </button>
      </div>
    );
  }

  return (
    <div className={`rounded-lg border border-red-200 bg-red-50 ${compact ? "p-3" : "p-4"}`}>
      <p className="text-sm text-red-600">{errorText}</p>
      <button
        type="button"
        onClick={onDismiss}
        className="mt-2 text-xs text-red-600 underline hover:text-red-800"
      >
        Dismiss
      </button>
    </div>
  );
};
